import lodashGet from "lodash/get";
import lodashSet from "lodash/set";
import { DateTime } from "luxon";
import React, { type ComponentPropsWithoutRef } from "react";
import {
  type PillarTableBaseColumnCellValuePathProps,
  type PillarTableEditColumnProps,
} from "shared/components/pillar-table/columns/PillarTableColumn";
import { usePillarTableContext } from "shared/components/pillar-table/PillarTableContext";

import { type PillarTableSafeAny } from "shared/components/pillar-table/PillarTableSafeAny";
import { usePillarTableRowContext } from "shared/components/pillar-table/row/PillarTableRowContext";
import {
  LuxonDateFormats,
  type AliasDateFormats,
  dateMapperFormats,
} from "shared/datetime/dateFormats";

export type PillarTableColumnDateTimeProps<
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
> = {
  format?: string | AliasDateFormats;
  nullValue?: string;
  isZoned?: boolean;
  useAliasFormat?: boolean;
} & PillarTableBaseColumnCellValuePathProps<RowGeneric> &
  ComponentPropsWithoutRef<"div">;

const PillarTableColumnDateTime = <
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
>({
  cellValuePath,
  format = LuxonDateFormats.D,
  nullValue,
  isZoned = false,
  useAliasFormat = false,
}: PillarTableColumnDateTimeProps<
  RowGeneric,
  ExtraPropsGeneric,
  InlineEditFormGeneric
>) => {
  const rowContext = usePillarTableRowContext();
  const value = lodashGet(rowContext?.rowValue, cellValuePath ?? "");
  const formattedValue = value
    ? DateTime.fromISO(value, { setZone: isZoned }).toFormat(
        useAliasFormat && typeof format === "string"
          ? dateMapperFormats[format as AliasDateFormats]
          : (format ?? LuxonDateFormats.D),
      )
    : (nullValue ?? "");

  return <>{formattedValue}</>;
};

const EditComponent = <
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
>({
  cellValuePath,
  formValuePath,
  testid,
}: PillarTableColumnDateTimeProps<
  RowGeneric,
  ExtraPropsGeneric,
  InlineEditFormGeneric
> &
  PillarTableEditColumnProps<
    RowGeneric,
    ExtraPropsGeneric,
    InlineEditFormGeneric
  >) => {
  const rowContext = usePillarTableRowContext<
    RowGeneric,
    ExtraPropsGeneric,
    InlineEditFormGeneric
  >();
  const tableContext = usePillarTableContext();

  const value = lodashGet(rowContext?.rowValue, cellValuePath ?? "");
  const [inputValue, setInputValue] = React.useState<string>(
    value && DateTime.fromISO(value as string).isValid
      ? DateTime.fromISO(value as string).toFormat("yyyy-MM-dd")
      : "",
  );
  return (
    <input
      type="date"
      name={cellValuePath}
      value={inputValue}
      data-testid={
        testid
          ? `${tableContext.testidTable}-table-row-${rowContext.rowIndex}-column-${testid}-edit-date`
          : undefined
      }
      className="rounded-md shadow-sm ring-1 ring-inset ring-neutral-mid-300 focus-within:ring-2 focus-within:ring-inset block bg-transparent !py-1 pl-1 text-neutral-mid-900 placeholder:text-neutral-mid-400 focus:ring-0 sm:text-sm sm:leading-relaxed"
      onChange={(e) => {
        setInputValue(e.target.value);
        rowContext.setFormValues!((prevValues: InlineEditFormGeneric) => {
          const newValues = { ...prevValues };
          lodashSet(
            newValues as object,
            formValuePath ?? cellValuePath!,
            e.target.value,
          );
          return newValues;
        });
      }}
    />
  );
};
EditComponent.isEditComponent = true;
PillarTableColumnDateTime.Edit = EditComponent;

export default PillarTableColumnDateTime;
